import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchCases } from '../lib/cms';
import { Shield, Zap, Flame, Eye, ArrowRight, Phone, Mail, MapPin, Award, CheckCircle2, Wind, Wrench, Coins } from 'lucide-react';
import SEO from '../components/SEO';

export default function Home() {
  const [cases, setCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(true);
  const [formStatus, setFormStatus] = useState({ type: null, message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function loadCases() {
      const data = await fetchCases();
      setCases(data.slice(0, 3)); // Show top 3 cases
      setLoadingCases(false);
    }
    loadCases();
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus({ type: null, message: '' });

    const formData = new FormData(e.target);
    const payload = Object.fromEntries(formData.entries());

    try {
      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setFormStatus({
          type: 'success',
          message: 'Tak for din henvendelse! Vi vender tilbage inden for 24 timer.',
        });
        e.target.reset();
      } else {
        throw new Error('Fejl ved indsendelse');
      }
    } catch (err) {
      setFormStatus({
        type: 'error',
        message: 'Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring til os.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": "https://detekniq.dk/#website",
        "url": "https://detekniq.dk/",
        "name": "D.E. TekniQ K/S",
        "description": "Din lokale el- og energispecialist i Midtjylland"
      },
      {
        "@type": "Electrician",
        "@id": "https://detekniq.dk/#organization",
        "name": "D.E. TekniQ K/S",
        "url": "https://detekniq.dk/",
        "logo": "https://assets.auxodev.dk/assets/c02e6411-90cc-481d-8aeb-e9d9691b4c94/de-logo-vek2-hvid2.png",
        "image": "https://assets.auxodev.dk/assets/c02e6411-90cc-481d-8aeb-e9d9691b4c94/varevogn.jpg",
        "telephone": "+4586667700",
        "email": "post@detekniq.dk",
        "priceRange": "$$",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Nørrevangsvej 5",
          "addressLocality": "Kjellerup",
          "postalCode": "8620",
          "addressCountry": "DK"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 56.2843,
          "longitude": 9.4356
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday"
            ],
            "opens": "07:30",
            "closes": "16:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "Friday",
            "opens": "07:30",
            "closes": "13:00"
          }
        ]
      }
    ]
  };

  return (
    <div className="relative">
      <SEO
        title="D.E. TekniQ K/S | Din lokale el- og energispecialist i Midtjylland"
        description="D.E. TekniQ K/S leverer professionelle, energirigtige og driftssikre løsninger til private, erhverv og landbrug i Kjellerup, Silkeborg, Viborg og omegn. Ring i dag!"
        schema={homeSchema}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-navy-950 py-24 text-white lg:py-32">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src="https://assets.auxodev.dk/assets/c02e6411-90cc-481d-8aeb-e9d9691b4c94/top-varmpumper.jpg"
            alt="D.E. TekniQ Varmepumper"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-900/90 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center flex flex-col items-center">
            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl leading-tight">
              Din lokale el- og energispecialist i Midtjylland
            </h1>
            <p className="mt-6 text-lg text-navy-100 max-w-xl leading-relaxed mx-auto">
              D.E. TekniQ K/S leverer professionelle, energirigtige og driftssikre løsninger til private, erhverv og landbrug i Kjellerup, Silkeborg, Viborg og omegn.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/kontakt"
                className="rounded-none bg-energy-600 px-6 py-3.5 text-base font-semibold text-white shadow-md transition-all duration-200 hover:bg-energy-700 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-energy-500"
              >
                Få uforpligtende tilbud
              </Link>
              <Link
                to="/varmepumper/daikin-luft-til-vand"
                className="rounded-none bg-navy-900/80 border border-navy-700 px-6 py-3.5 text-base font-semibold text-white shadow-md transition-all duration-200 hover:bg-navy-800 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-navy-500"
              >
                Læs om varmepumper
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Shortcuts (Varmepumper, El-arbejde, Alarmer) */}
      <section className="relative z-20 -mt-12 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Varmepumper */}
          <div className="rounded-none border border-navy-100 bg-white p-8 shadow-xl transition-all duration-300 hover:shadow-2xl">
            <div className="inline-flex rounded-none bg-energy-50 p-3 text-energy-600">
              <Flame className="h-6 w-6" />
            </div>
            <h3 className="mt-6 text-xl font-bold text-navy-950">Varmepumper</h3>
            <p className="mt-3 text-sm text-graphite-600 leading-relaxed">
              Spar op til 60% på dine varmeudgifter. Vi monterer og servicerer luft til vand og luft til luft varmepumper.
            </p>
            <Link
              to="/varmepumper/daikin-luft-til-vand"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-energy-600 hover:text-energy-700"
            >
              Læs mere <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* El-arbejde */}
          <div className="rounded-none border border-navy-100 bg-white p-8 shadow-xl transition-all duration-300 hover:shadow-2xl">
            <div className="inline-flex rounded-none bg-navy-50 p-3 text-navy-600">
              <Zap className="h-6 w-6" />
            </div>
            <h3 className="mt-6 text-xl font-bold text-navy-950">El-installation</h3>
            <p className="mt-3 text-sm text-graphite-600 leading-relaxed">
              Fra traditionelt el-arbejde og fejlfinding to ladebokse til elbil og intelligente IHC Smart Home-løsninger.
            </p>
            <Link
              to="/el-installation/traditionelt-el-arbejde"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-navy-600 hover:text-navy-700"
            >
              Læs mere <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* Alarmer */}
          <div className="rounded-none border border-navy-100 bg-white p-8 shadow-xl transition-all duration-300 hover:shadow-2xl">
            <div className="inline-flex rounded-none bg-red-50 p-3 text-red-600">
              <Eye className="h-6 w-6" />
            </div>
            <h3 className="mt-6 text-xl font-bold text-navy-950">Sikring & Alarmer</h3>
            <p className="mt-3 text-sm text-graphite-600 leading-relaxed">
              Få tryghed med professionelle alarmsystemer, TV-overvågning, GPS-sporing samt stald- og gyllealarmer.
            </p>
            <Link
              to="/sikring-og-netvaerk/alarmsystemer-og-overvaagning"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-red-600 hover:text-red-700"
            >
              Læs mere <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Welcome & Presentation */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-stretch">
            {/* Left Column: Text */}
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-energy-600">Velkommen til D.E. TekniQ K/S</span>
              <h2 className="mt-3 text-3xl font-bold text-navy-950 sm:text-4xl md:leading-tight">
                Hos D.E. TekniQ går vi op i tilfredse kunder
              </h2>
              <p className="mt-6 text-base text-graphite-700 leading-relaxed">
                Hos D.E. TekniQ har vi ét ønske – at skabe glade og tilfredse kunder. Vi har foretaget en kundetilfredshedsanalyse på en håndfuld af vores kunder, og tendensen er entydig – de er tilfredse med deres samarbejde. Faktisk er over 95% af vores kunder enten meget eller godt tilfredse med det arbejde, vi har udført.
              </p>
              <p className="mt-4 text-base text-graphite-700 leading-relaxed">
                Med mere end 25 års erfaring med el-relaterede opgaver står vi klar til at hjælpe dig. Vi løser alt fra små opgaver i private hjem to avancerede elinstallationer og energirådgivning til erhverv og landbrug.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-5 w-5 text-energy-500 shrink-0" />
                  <span className="text-sm font-semibold text-navy-950">ISO Certificeret</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-5 w-5 text-energy-500 shrink-0" />
                  <span className="text-sm font-semibold text-navy-950">Medlem af TEKNIQ</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-5 w-5 text-energy-500 shrink-0" />
                  <span className="text-sm font-semibold text-navy-950">Registreret Energivejleder</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-5 w-5 text-energy-500 shrink-0" />
                  <span className="text-sm font-semibold text-navy-950">KMO Godkendt</span>
                </div>
              </div>
            </div>

            {/* Right Column: Image */}
            <div className="relative h-full">
              <div className="h-full min-h-[350px] overflow-hidden rounded-none bg-navy-100 shadow-xl">
                <img
                  src="https://assets.auxodev.dk/assets/c02e6411-90cc-481d-8aeb-e9d9691b4c94/varevogn.jpg"
                  alt="D.E. TekniQ Servicebil"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Video Section */}
      <section className="bg-navy-950 py-16 text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-wider text-energy-400">Se vores præsentationsvideo</span>
          <h2 className="mt-3 text-2xl font-bold sm:text-3xl mb-8">
            Hør mere om vores energiløsninger og varmepumper
          </h2>
          <div className="relative aspect-video w-full shadow-2xl border border-navy-800">
            <iframe
              className="absolute inset-0 h-full w-full"
              src="https://www.youtube.com/embed/6qw9ZOriy-k?si=TAXLTiF6dQVXS1Fy"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      {/* Varmepumpe Fokusområder Section */}
      <section className="bg-navy-50 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-energy-600">Varmepumper</span>
              <h2 className="mt-3 text-3xl font-bold text-navy-950 sm:text-4xl">
                Fokusområder inden for varmepumper
              </h2>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {/* Box 1: Daikin Luft til Vand */}
            <Link
              to="/varmepumper/daikin-luft-til-vand"
              className="group flex flex-col bg-white p-8 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl rounded-none border-t-4 border-energy-500"
            >
              <div className="inline-flex w-fit rounded-none bg-energy-50 p-3 text-energy-600">
                <Flame className="h-6 w-6" />
              </div>
              <h3 className="mt-6 text-xl font-bold text-navy-950 group-hover:text-energy-600 transition-colors">
                Daikin Luft til Vand
              </h3>
              <p className="mt-3 flex-1 text-sm text-graphite-600 leading-relaxed">
                Fremtidssikret og energieffektiv opvarmning med Daikin Altherma. Spar på varmeregningen og styr det hele med Onecta-appen.
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-energy-600">
                Læs mere <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            {/* Box 2: Luft til Luft Varmepumper */}
            <Link
              to="/varmepumper/luft-til-luft"
              className="group flex flex-col bg-white p-8 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl rounded-none border-t-4 border-navy-600"
            >
              <div className="inline-flex w-fit rounded-none bg-navy-50 p-3 text-navy-600">
                <Wind className="h-6 w-6" />
              </div>
              <h3 className="mt-6 text-xl font-bold text-navy-950 group-hover:text-navy-600 transition-colors">
                Luft til Luft Varmepumper
              </h3>
              <p className="mt-3 flex-1 text-sm text-graphite-600 leading-relaxed">
                Perfekt til sommerhuse, garager og som supplerende varme i parcelhuse. Få et sundt indeklima og effektiv komfort hele året.
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-navy-600">
                Læs mere <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            {/* Box 3: Service & Vedligehold */}
            <Link
              to="/varmepumper/service-og-vedligehold"
              className="group flex flex-col bg-white p-8 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl rounded-none border-t-4 border-energy-500"
            >
              <div className="inline-flex w-fit rounded-none bg-energy-50 p-3 text-energy-600">
                <Wrench className="h-6 w-6" />
              </div>
              <h3 className="mt-6 text-xl font-bold text-navy-950 group-hover:text-energy-600 transition-colors">
                Service & Vedligehold
              </h3>
              <p className="mt-3 flex-1 text-sm text-graphite-600 leading-relaxed">
                Lovpligtigt årligt serviceeftersyn sikrer optimal driftssikkerhed, længere levetid og maksimal effektivitet på din varmepumpe.
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-energy-600">
                Læs mere <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            {/* Box 4: Tilskud & Finansiering */}
            <Link
              to="/varmepumper/tilskud-og-finansiering"
              className="group flex flex-col bg-white p-8 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl rounded-none border-t-4 border-navy-600"
            >
              <div className="inline-flex w-fit rounded-none bg-navy-50 p-3 text-navy-600">
                <Coins className="h-6 w-6" />
              </div>
              <h3 className="mt-6 text-xl font-bold text-navy-950 group-hover:text-navy-600 transition-colors">
                Tilskud & Finansiering
              </h3>
              <p className="mt-3 flex-1 text-sm text-graphite-600 leading-relaxed">
                Få hjælp til Varmepumpepuljen 2026, udnyt dine tilskudsmuligheder og find den rette finansieringsløsning til dit projekt.
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-navy-600">
                Læs mere <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
            {/* Contact Info */}
            <div className="lg:col-span-5">
              <span className="text-xs font-bold uppercase tracking-wider text-energy-600">Kontakt os i dag</span>
              <h2 className="mt-3 text-3xl font-bold text-navy-950 sm:text-4xl">
                Lad os tage en uforpligtende snak
              </h2>
              <p className="mt-6 text-base text-graphite-700 leading-relaxed">
                Står du overfor et el-projekt, overvejer du at skifte til en energirigtig varmepumpe, eller har du brug for at få sikret din bolig eller virksomhed? Vi står klar med professionel rådgivning og et uforpligtende tilbud.
              </p>

              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="rounded-none bg-energy-50 p-3 text-energy-600">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-navy-950">Adresse</h4>
                    <p className="text-sm text-graphite-600 mt-1">Nørrevangsvej 5, 8620 Kjellerup</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="rounded-none bg-energy-50 p-3 text-energy-600">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-navy-950">Telefon</h4>
                    <a href="tel:+4586667700" className="text-sm text-graphite-600 hover:text-energy-600 transition-colors mt-1 block whitespace-nowrap">
                      +45 86 66 77 00
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="rounded-none bg-energy-50 p-3 text-energy-600">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-navy-950">E-mail</h4>
                    <a href="mailto:post@detekniq.dk" className="text-sm text-graphite-600 hover:text-energy-600 transition-colors mt-1 block whitespace-nowrap">
                      post@detekniq.dk
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="rounded-none border border-navy-100 bg-white p-8 shadow-xl lg:col-span-7">
              <h3 className="text-xl font-bold text-navy-950">Få et uforpligtende tilbud inden for 24 timer</h3>
              <p className="text-sm text-graphite-600 mt-2">Udfyld formularen nedenfor, så kontakter vi dig hurtigst muligt.</p>

              <form onSubmit={handleFormSubmit} className="mt-8 space-y-6">
                {/* Antispam Honeypot Field */}
                <div className="sr-only absolute" aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-navy-950">Navn</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="mt-2 block w-full rounded-none border border-navy-100 bg-navy-50/50 px-4 py-3 text-sm text-navy-950 placeholder-graphite-400 focus:border-energy-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-energy-500/20"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-navy-950">Telefonnummer</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="mt-2 block w-full rounded-none border border-navy-100 bg-navy-50/50 px-4 py-3 text-sm text-navy-950 placeholder-graphite-400 focus:border-energy-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-energy-500/20"
                      placeholder="Dit telefonnummer"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-navy-950">E-mail</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="mt-2 block w-full rounded-none border border-navy-100 bg-navy-50/50 px-4 py-3 text-sm text-navy-950 placeholder-graphite-400 focus:border-energy-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-energy-500/20"
                    placeholder="din.email@adresse.dk"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-navy-950">Beskrivelse</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    required
                    className="mt-2 block w-full rounded-none border border-navy-100 bg-navy-50/50 px-4 py-3 text-sm text-navy-950 placeholder-graphite-400 focus:border-energy-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-energy-500/20"
                    placeholder="Beskriv din opgave eller dit projekt..."
                  ></textarea>
                </div>

                {formStatus.message && (
                  <div className={`rounded-none p-4 text-sm font-medium ${formStatus.type === 'success' ? 'bg-energy-50 text-energy-800' : 'bg-red-50 text-red-800'}`}>
                    {formStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-none bg-energy-600 py-4 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-energy-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-energy-500 disabled:opacity-50"
                >
                  {submitting ? 'Sender...' : 'Send besked'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
